import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, Image as ImageIcon, RefreshCw, RotateCcw, Trash2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { jobLabel, publicPipelineError } from "@/lib/pipeline";
import { StatusBadge } from "@/components/States";
import { Button } from "@/components/ui/button";

export function PackReviewContent({ data, fileRef, uploading, onUpload, onRefresh, onReprocess, onOpenOriginal, onRetry, onRemove }) {
  const { pack, documents, topics, question_count: questionCount, jobs } = data;
  return (
    <div className="space-y-6" data-testid="pack-review-page">
      <Link to="/p/material" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500">
        <ArrowLeft className="h-4 w-4" /> Volver al material
      </Link>
      <ReviewHeader
        pack={pack}
        documents={documents}
        questionCount={questionCount}
        fileRef={fileRef}
        uploading={uploading}
        onUpload={onUpload}
        onRefresh={onRefresh}
        onReprocess={onReprocess}
      />
      <PackMessages pack={pack} />
      <NotesQuality quality={pack.notes_quality} />
      <DocumentsSection documents={documents} onOpenOriginal={onOpenOriginal} onRetry={onRetry} onRemove={onRemove} />
      <TopicsSection topics={topics} />
      <JobsSection pack={pack} jobs={jobs} />
    </div>
  );
}

function ReviewHeader({ pack, documents, questionCount, fileRef, uploading, onUpload, onRefresh, onReprocess }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{pack.title}</h1>
        <div className="mt-2 flex items-center gap-3">
          <StatusBadge status={pack.status} testId="review-pack-status" />
          <span className="text-sm text-slate-500">{questionCount} ejercicios generados</span>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(event) => onUpload(event.target.files)}
          data-testid="pack-file-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="estudia-touch rounded-xl bg-blue-600 font-semibold hover:bg-blue-700"
          data-testid="pack-upload-btn"
        >
          <Upload className="mr-2 h-4 w-4" />
          {uploading ? "Subiendo..." : "Subir paginas"}
        </Button>
        <Button variant="outline" onClick={onRefresh} className="estudia-touch rounded-xl" data-testid="review-refresh-btn" aria-label="Actualizar revisión">
          <RefreshCw className="h-4 w-4" />
        </Button>
        {documents.some((document) => document.status === "ready") && (
          <Button variant="outline" onClick={onReprocess} className="estudia-touch rounded-xl font-semibold" data-testid="pack-reprocess-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reprocesar
          </Button>
        )}
      </div>
    </div>
  );
}

function PackMessages({ pack }) {
  return (
    <>
      {pack.status === "failed" && (
        <div className="estudia-card border-rose-200 bg-rose-50/60 p-4" data-testid="review-pack-error">
          <p className="flex items-center gap-2 text-sm font-semibold text-rose-800">
            <AlertTriangle className="h-4 w-4" /> Este material no pudo procesarse
          </p>
          <p className="mt-1 text-sm text-rose-700">{publicPipelineError(pack.last_error)}</p>
        </div>
      )}
      {(pack.warnings || []).length > 0 && (
        <div className="estudia-card border-amber-200 bg-amber-50/60 p-4" data-testid="review-warnings">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4" /> Avisos del procesamiento
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-800">
            {pack.warnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function NotesQuality({ quality }) {
  if (!quality) return null;
  return (
    <div className="estudia-card p-4 text-sm" data-testid="review-notes-quality">
      <p className="font-display font-bold text-slate-900">Calidad de los apuntes</p>
      {quality.assessable ? (
        <>
          <p className="mt-1 text-slate-600">
            Señales observables: {quality.score}/{quality.max_score} · {quality.signals.enough_content ? "contenido suficiente" : "contenido escaso"},{" "}
            {quality.signals.organized ? "bien organizado" : "organizacion mejorable"}. No evaluamos la caligrafia.
          </p>
          {quality.reward_coins > 0 && (
            <p className="mt-2 font-semibold text-emerald-700">
              {quality.reward_awarded
                ? `Recompensa otorgada: +${quality.reward_coins} monedas`
                : "Recompensa no otorgada porque el modo juego estaba desactivado"}
            </p>
          )}
        </>
      ) : (
        <p className="mt-1 text-slate-600">{quality.reason}</p>
      )}
    </div>
  );
}

function DocumentsSection({ documents, onOpenOriginal, onRetry, onRemove }) {
  return (
    <section data-testid="review-documents">
      <h2 className="font-display text-xl font-bold text-slate-900">Paginas procesadas</h2>
      {documents.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">Todavia no hay paginas. Sube fotos del cuaderno.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {documents.map((document) => (
            <DocumentCard key={document.id} doc={document} onOpenOriginal={onOpenOriginal} onRetry={onRetry} onRemove={onRemove} />
          ))}
        </div>
      )}
    </section>
  );
}

function DocumentCard({ doc, onOpenOriginal, onRetry, onRemove }) {
  return (
    <div className="estudia-card p-4" data-testid="review-document-row">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <DocumentThumbnail doc={doc} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DocumentIdentity doc={doc} />
            <DocumentActions doc={doc} onOpenOriginal={onOpenOriginal} onRetry={onRetry} onRemove={onRemove} />
          </div>
          {doc.last_error && (
            <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">
              Esta pagina no pudo procesarse. Puedes reintentar, reemplazarla o continuar con las demas.
            </p>
          )}
          {(doc.warnings || []).length > 0 && (
            <ul className="mt-3 list-inside list-disc text-xs text-amber-700">
              {doc.warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          )}
          {doc.ocr_text && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-blue-700">Ver texto interpretado</summary>
              <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-xs text-slate-700">{doc.ocr_text}</pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentIdentity({ doc }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 font-display text-sm font-bold text-slate-600">{doc.page_index}</span>
      <div>
        <p className="text-sm font-semibold text-slate-800">{doc.original_filename}</p>
        <p className="text-xs text-slate-600">
          {doc.ocr_confidence !== null ? `Confianza ${Math.round(doc.ocr_confidence * 100)}%` : "Sin procesar"}
        </p>
      </div>
    </div>
  );
}

function DocumentActions({ doc, onOpenOriginal, onRetry, onRemove }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <StatusBadge status={doc.status} />
      <Button variant="outline" size="sm" onClick={() => onOpenOriginal(doc)} data-testid={`open-document-${doc.page_index}`}>
        <Eye className="mr-1 h-3.5 w-3.5" /> Original
      </Button>
      {(doc.status === "failed" || doc.needs_review) && (
        <Button variant="outline" size="sm" onClick={() => onRetry(doc.id)} data-testid={`retry-document-${doc.page_index}`}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reintentar
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => onRemove(doc.id)} data-testid={`delete-document-${doc.page_index}`} aria-label="Descartar pagina">
        <Trash2 className="h-4 w-4 text-slate-400" />
      </Button>
    </div>
  );
}

function TopicsSection({ topics }) {
  return (
    <section data-testid="review-topics">
      <h2 className="font-display text-xl font-bold text-slate-900">Temas encontrados</h2>
      {topics.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">Los temas aparecen cuando el procesamiento termina de estructurar el material.</p>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {topics.map((topic) => <TopicCard key={topic.id} topic={topic} />)}
        </div>
      )}
    </section>
  );
}

function TopicCard({ topic }) {
  return (
    <div className="estudia-card p-4" data-testid="review-topic-card">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-base font-bold text-slate-900">{topic.name}</h3>
        {topic.needs_review ? (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700">
            <AlertTriangle className="h-3 w-3" /> Revisar
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
            <CheckCircle2 className="h-3 w-3" /> {Math.round(topic.confidence * 100)}%
          </span>
        )}
      </div>
      {topic.summary && <p className="mt-2 text-sm text-slate-600">{topic.summary}</p>}
      {topic.key_points.length > 0 && (
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
          {topic.key_points.map((point) => <li key={point}>{point}</li>)}
        </ul>
      )}
      {topic.needs_review && (
        <p className="mt-3 text-xs text-amber-700">No generamos ejercicios de este tema porque la interpretacion no es fiable.</p>
      )}
    </div>
  );
}

function JobsSection({ pack, jobs }) {
  const failedJobs = pack.status === "failed" ? jobs.filter((job) => job.status === "failed") : [];
  const hasDownstreamFailure = failedJobs.some((job) => job.job_type !== "process_document");
  return (
    <section data-testid="review-jobs">
      <h2 className="font-display text-xl font-bold text-slate-900">Procesamiento</h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="pb-2">Tarea</th><th className="pb-2">Estado</th><th className="pb-2">Intentos</th><th className="pb-2">Duracion</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map((job) => (
              <tr key={job.id} data-testid="review-job-row">
                <td className="py-2 font-medium text-slate-700">{jobLabel(job.job_type)}</td>
                <td className="py-2"><StatusBadge status={job.status} /></td>
                <td className="py-2">{job.attempts}/{job.max_attempts}</td>
                <td className="py-2 text-slate-500">{job.duration_ms ? `${job.duration_ms} ms` : "—"}</td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={4} className="py-3 text-slate-500">Sin tareas registradas.</td></tr>}
          </tbody>
        </table>
      </div>
      {failedJobs.length > 0 && (
        <p className="mt-3 text-xs text-rose-700" data-testid="review-failed-jobs">
          {failedJobs.length} tarea(s) fallida(s).{" "}
          {hasDownstreamFailure
            ? 'Si las paginas ya fueron leidas, usa "Reprocesar" para continuar desde la interpretacion guardada.'
            : 'Usa "Reintentar" en la pagina afectada.'}
        </p>
      )}
    </section>
  );
}

function DocumentThumbnail({ doc }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!doc.thumbnail_url) {
      setSrc("");
      return undefined;
    }
    let active = true;
    let objectUrl = "";
    api.get(doc.thumbnail_url, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc("");
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.thumbnail_url]);

  return (
    <div className="grid h-36 w-full shrink-0 place-items-center overflow-hidden rounded-2xl bg-slate-100 sm:w-28">
      {src ? (
        <img src={src} alt={`Vista previa de ${doc.original_filename}`} className="h-full w-full object-cover" data-testid={`document-thumbnail-${doc.page_index}`} />
      ) : (
        <ImageIcon className="h-7 w-7 text-slate-300" aria-hidden="true" />
      )}
    </div>
  );
}
